import React, { forwardRef, useState } from 'react';
import { Dropdown, Badge, ListGroup, Button, Spinner } from 'react-bootstrap';
import { useNotification } from '../context/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { encodeId } from '../utils/idEncoder';

// Custom Toggle to render the Bell Icon
const NotificationToggle = forwardRef(({ children, onClick, count, size = 38, darkMode = false }, ref) => (
    <div
        ref={ref}
        onClick={(e) => {
            e.preventDefault();
            onClick(e);
        }}
        style={{ cursor: 'pointer', position: 'relative', display: 'inline-block' }}
        className="notification-toggle text-reset"
    >
        <div 
            className={`rounded-circle d-flex align-items-center justify-content-center transition-all ${darkMode 
                ? 'btn-outline-secondary text-light border border-secondary' 
                : 'bg-body-tertiary text-body border border-light-subtle'
            }`}
            style={{ width: `${size}px`, height: `${size}px` }}
        >
            <i className="bi bi-bell-fill fs-6"></i>
        </div>
        {count > 0 && (
            <Badge
                bg="danger"
                pill
                style={{
                    position: 'absolute',
                    top: '-2px',
                    right: '-2px',
                    fontSize: '10px',
                    padding: '2px 5px',
                    boxShadow: '0 0 0 2px var(--bs-body-bg)'
                }}
            >
                {count > 99 ? '99+' : count}
            </Badge>
        )}
        {children}
    </div>
));

const NotificationDropdown = ({ size = 38, darkMode = false }) => {
    const {
        notifications,
        unreadCount,
        isLoading,
        error,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        refreshNotifications
    } = useNotification();

    const navigate = useNavigate();
    const [show, setShow] = useState(false);

    // Icon mapping based on notification type
    const getIcon = (type) => {
        const iconMap = {
            opportunity: 'bi-briefcase',
            task: 'bi-check2-square',
            customer: 'bi-people',
            performance: 'bi-graph-up',
            system: 'bi-info-circle',
            collaboration: 'bi-chat-dots',
            ticket: 'bi-ticket-perforated',
            visit: 'bi-calendar-event'
        };

        const colorMap = {
            opportunity: 'text-primary',
            task: 'text-info',
            customer: 'text-success',
            performance: 'text-warning',
            system: 'text-info',
            collaboration: 'text-primary',
            ticket: 'text-info',
            visit: 'text-primary'
        };

        const icon = iconMap[type] || 'bi-bell';
        const color = colorMap[type] || 'text-secondary';

        return <i className={`${icon} ${color}`}></i>;
    };

    // Format timestamp
    const formatTime = (isoString) => {
        if (!isoString) return '';

        const date = new Date(isoString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.round(diffMs / 60000);
        const diffHrs = Math.round(diffMs / 3600000);
        const diffDays = Math.round(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHrs < 24) return `${diffHrs}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    // Handle notification click
    const handleNotificationClick = async (notif) => {
        // Mark as read if not already
        if (!notif.is_read) {
            await markAsRead(notif.id);
        }

        setShow(false); // Close dropdown

        // 1. If we have a related_id and a recognized type, use encoded navigation
        if (notif.related_id) {
            const encodedId = encodeId(notif.related_id);

            switch (notif.type) {
                case 'ticket':
                    navigate(`/tickets/${encodedId}`);
                    return;
                case 'opportunity':
                    navigate(`/opportunities/${encodedId}`);
                    return;
                case 'customer':
                    navigate(`/customers/${encodedId}`);
                    return;
                case 'visit':
                    navigate(`/sales-visits/${encodedId}`);
                    return;
                case 'task':
                    // If task detail page exists, go there, otherwise go to task list
                    navigate(`/tasks`);
                    return;
                default:
                    // Fallback handled below
                    break;
            }
        }

        // 2. Specialized handling for types without related_id or specific needs
        if (notif.type === 'performance') {
            navigate('/performance');
            return;
        }

        if (notif.type === 'task') {
            navigate('/tasks');
            return;
        }

        // 3. Fallback: Use action_url if recognized or provided by backend
        if (notif.action_url) {
            // Clean up common backend action_url mismatches
            let url = notif.action_url;
            if (url.startsWith('/visits/')) url = url.replace('/visits/', '/sales-visits/');

            navigate(url);
        }
    };

    // Handle dropdown toggle
    const handleToggle = (isOpen) => {
        setShow(isOpen);
        if (isOpen) {

            // Refresh notifications when opening dropdown
            refreshNotifications();
        }
    };

    return (
        <Dropdown align="end" show={show} onToggle={handleToggle}>
            <Dropdown.Toggle as={NotificationToggle} count={unreadCount} size={size} darkMode={darkMode} id="notification-dropdown" />

            <Dropdown.Menu className="shadow-lg border-0 rounded-4 p-0 mt-2" style={{ width: '360px', maxHeight: '500px', overflowY: 'auto' }}>
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom sticky-top bg-body">
                    <h6 className="mb-0 fw-bold">
                        Notifications
                        {unreadCount > 0 && (
                            <Badge bg="danger" pill className="ms-2" style={{ fontSize: '0.7rem' }}>
                                {unreadCount}
                            </Badge>
                        )}
                    </h6>
                    <div className="d-flex gap-2">
                        <Button
                            variant="link"
                            size="sm"
                            className="text-decoration-none p-0 text-muted"
                            onClick={() => {
                                setShow(false);
                                navigate('/notification-settings');
                            }}
                            title="Settings"
                        >
                            <i className="bi bi-gear"></i>
                        </Button>
                        <Button
                            variant="link"
                            size="sm"
                            className="text-decoration-none p-0"
                            onClick={refreshNotifications}
                            title="Refresh"
                        >
                            <i className="bi bi-arrow-clockwise"></i>
                        </Button>
                        {unreadCount > 0 && (
                            <Button
                                variant="link"
                                size="sm"
                                className="text-decoration-none p-0"
                                onClick={async () => {
                                    await markAllAsRead();
                                    // Close dropdown after marking all as read
                                    setTimeout(() => setShow(false), 1000);
                                }}
                            >
                                Mark all read
                            </Button>
                        )}
                    </div>
                </div>

                {/* Loading State */}
                {isLoading && (
                    <div className="text-center py-4">
                        <Spinner animation="border" size="sm" variant="primary" />
                        <p className="text-muted small mt-2 mb-0">Loading notifications...</p>
                    </div>
                )}

                {/* Error State */}
                {error && !isLoading && (
                    <div className="text-center py-4 px-3">
                        <i className="bi bi-exclamation-triangle text-danger fs-3"></i>
                        <p className="text-danger small mt-2 mb-0">{error}</p>
                        <Button
                            variant="link"
                            size="sm"
                            onClick={refreshNotifications}
                            className="mt-2"
                        >
                            Try again
                        </Button>
                    </div>
                )}

                {/* Empty State */}
                {!isLoading && !error && notifications.length === 0 && (
                    <div className="text-center py-4 px-3">
                        <i className="bi bi-bell-slash text-muted fs-3"></i>
                        <p className="text-muted small mt-2 mb-0">No notifications</p>
                    </div>
                )}

                {/* Notification List */}
                {!error && notifications.length > 0 && (
                    <ListGroup variant="flush">
                        {notifications.map((notif) => (
                            <ListGroup.Item
                                key={notif.id}
                                action
                                as="div"
                                className={`px-3 py-2 position-relative ${!notif.is_read ? 'bg-primary-subtle' : ''}`}
                                onClick={() => handleNotificationClick(notif)}
                                style={{
                                    cursor: 'pointer',
                                    borderLeft: !notif.is_read ? '3px solid #0d6efd' : '3px solid transparent',
                                    backgroundColor: !notif.is_read ? 'rgba(13, 110, 253, 0.05)' : 'transparent'
                                }}
                            >
                                <div className="d-flex align-items-start">
                                    <div className="me-3 mt-1 fs-5">
                                        {getIcon(notif.type)}
                                    </div>
                                    <div className="flex-grow-1">
                                        <p className="mb-1 fw-semibold small text-reset">
                                            {notif.title}
                                        </p>
                                        {notif.message && (
                                            <p className="mb-1 text-secondary small" style={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden'
                                            }}>
                                                {notif.message}
                                            </p>
                                        )}
                                        <div className="d-flex justify-content-between align-items-center">
                                            <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                                {formatTime(notif.created_at)}
                                            </small>
                                            {notif.priority === 'urgent' && (
                                                <Badge bg="danger" className="ms-2">Urgent</Badge>
                                            )}
                                            {notif.priority === 'high' && (
                                                <Badge bg="warning" className="ms-2">High</Badge>
                                            )}
                                        </div>
                                    </div>
                                    {!notif.is_read && (
                                        <span
                                            className="position-absolute end-0 me-3 mt-2 p-1 bg-primary rounded-circle"
                                            style={{ width: '8px', height: '8px' }}
                                        >
                                            <span className="visually-hidden">Unread</span>
                                        </span>
                                    )}

                                    {/* Delete button */}
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="position-absolute end-0 bottom-0 me-2 mb-1 p-0 text-muted"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            deleteNotification(notif.id);
                                        }}
                                        title="Delete"
                                        style={{ fontSize: '0.8rem' }}
                                    >
                                        <i className="bi bi-x-lg"></i>
                                    </Button>
                                </div>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                )}

                {/* Footer */}
                <div className="text-center py-2 border-top bg-white sticky-bottom">
                    <Button
                        variant="link"
                        size="sm"
                        className="text-decoration-none text-muted"
                        onClick={() => {
                            setShow(false);
                            navigate('/notifications');
                        }}
                    >
                        View all notifications
                    </Button>
                </div>
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default NotificationDropdown;
